import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { apiConfig } from '../config/api';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const ChatContainer = styled(motion.div)`
  position: relative;
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  box-shadow: ${props => props.theme.shadows.heavy};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  max-width: 400px;
  width: 100%;
`;

const ChatToggle = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.medium};
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ChatWindow = styled(motion.div)`
  height: 500px;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.surface};
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  display: flex;
  align-items: center;
  justify-content: between;

  h3 {
    color: white;
    font-size: 16px;
    font-weight: 600;
    flex: 1;
  }

  button {
    background: none;
    color: white;
    font-size: 18px;
    padding: 4px;
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
  }
`;

const Message = styled(motion.div)`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.4;
  
  ${props => props.isUser ? `
    align-self: flex-end;
    background: linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.secondary});
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background: ${props.theme.colors.background};
    color: ${props.theme.colors.text};
    border: 1px solid ${props.theme.colors.border};
    border-bottom-left-radius: 4px;
  `}
`;

const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  gap: 8px;
  align-items: center;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 24px;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.disabled ? props.theme.colors.border : props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.secondary};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const TypingIndicator = styled.div`
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: ${props => props.theme.colors.background};
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  border: 1px solid ${props => props.theme.colors.border};
  
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.theme.colors.textLight};
    animation: ${pulse} 1.4s infinite;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const QuickAction = styled.button`
  padding: 6px 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  font-size: 12px;
  color: ${props => props.theme.colors.text};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const ChatInterface = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm Ozy, your health optimization assistant. Tell me what you ate, how you worked out, or ask me anything about your health goals.",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const response = await axios.post(`${apiConfig.baseURL}${apiConfig.endpoints.chat}`, {
        message: userMessage.text,
        userId: 'temp_user', // TODO: Get from auth
        context: {
          previousMessages: messages.slice(-5), // Last 5 messages for context
          timestamp: new Date().toISOString()
        }
      });

      // Simulate typing delay
      setTimeout(() => {
        setIsTyping(false);
        
        // Handle different response types
        let messageText = response.data.response || "I understand. Let me process that information.";
        let suggestions = response.data.suggestions || [];
        
        // Add specific handling for meal logs
        if (response.data.type === 'meal_log' && response.data.extracted_data) {
          const meal = response.data.extracted_data;
          const nutrition = meal.total_nutrition;
          
          if (nutrition) {
            messageText += `\n\n📊 Nutrition Summary:\n• Calories: ${nutrition.calories || 0}\n• Protein: ${nutrition.protein || 0}g\n• Carbs: ${nutrition.carbs || 0}g\n• Fat: ${nutrition.fat || 0}g`;
            if (nutrition.fiber) messageText += `\n• Fiber: ${nutrition.fiber}g`;
          }
        }
        
        // Add quick actions based on response type
        if (response.data.type === 'meal_log') {
          suggestions = [...suggestions, "Check my macros", "Plan my next meal"];
        } else if (response.data.type === 'workout_plan') {
          suggestions = [...suggestions, "Log my workout", "Adjust the plan"];
        }
        
        const assistantMessage = {
          id: Date.now() + 1,
          text: messageText,
          isUser: false,
          timestamp: new Date(),
          data: response.data.extracted_data,
          suggestions: suggestions,
          type: response.data.type
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
      }, 1500);

    } catch (error) {
      setIsTyping(false);
      setIsLoading(false);
      
      // Handle specific error types
      let errorText = "I'm having trouble processing that right now. Please try again.";
      let toastMessage = 'Failed to send message';
      
      if (error.response?.data?.type === 'service_error') {
        errorText = "My AI processing service is temporarily unavailable. Please check with your administrator.";
        toastMessage = 'AI service unavailable';
      } else if (error.response?.data?.type === 'processing_error') {
        errorText = error.response.data.error || errorText;
      } else if (error.response?.status === 400) {
        errorText = "I didn't understand that message format. Could you try rephrasing?";
        toastMessage = 'Invalid message format';
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      toast.error(toastMessage);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    "Log my breakfast",
    "Plan today's workout",
    "Check my macros",
    "Weight update"
  ];

  const handleQuickAction = (action) => {
    setInputValue(action);
    inputRef.current?.focus();
  };

  if (!isOpen) {
    return (
      <ChatToggle onClick={() => setIsOpen(true)}>
        💬
      </ChatToggle>
    );
  }

  return (
    <ChatContainer
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: "spring", duration: 0.3 }}
    >
      <ChatWindow>
        <ChatHeader>
          <h3>🏛️ Ozy - Health Assistant</h3>
          <button onClick={() => setIsOpen(false)}>×</button>
        </ChatHeader>

        <MessagesContainer>
          <AnimatePresence>
            {messages.map((message) => (
              <Message
                key={message.id}
                isUser={message.isUser}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {message.text}
                {message.suggestions && message.suggestions.length > 0 && (
                  <QuickActions>
                    {message.suggestions.map((suggestion, index) => (
                      <QuickAction
                        key={index}
                        onClick={() => handleQuickAction(suggestion)}
                      >
                        {suggestion}
                      </QuickAction>
                    ))}
                  </QuickActions>
                )}
              </Message>
            ))}
          </AnimatePresence>

          {isTyping && (
            <TypingIndicator>
              <span></span>
              <span></span>
              <span></span>
            </TypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <MessageInput
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Tell me about your meals, workouts, or goals..."
            disabled={isLoading}
          />
          <SendButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
          >
            ➤
          </SendButton>
        </InputContainer>

        {messages.length <= 1 && (
          <div style={{ padding: '0 16px 16px' }}>
            <QuickActions>
              {quickActions.map((action, index) => (
                <QuickAction
                  key={index}
                  onClick={() => handleQuickAction(action)}
                >
                  {action}
                </QuickAction>
              ))}
            </QuickActions>
          </div>
        )}
      </ChatWindow>
    </ChatContainer>
  );
};

export default ChatInterface;